import { useCallback, useEffect, useRef, useState } from "react";
import {
  addScanExclusion,
  exclusionErrorKey,
  listScanExclusions,
  removeScanExclusion,
  type ScanExclusion,
} from "../../../../lib/scanExclusions";

/**
 * The do-not-scan list's state: the list, and the two things an operator can
 * do with it (add a site, take one off).
 *
 * A failed read never throws: it becomes a flag the panel turns into a
 * sentence and a retry button, the same way the lead watch's strip does. After
 * every change the list is read again rather than patched in place, because an
 * add may answer with somebody else's older entry for the same site.
 */

export type ExclusionNotice =
  | { kind: "added"; domain: string }
  | { kind: "already"; domain: string }
  | { kind: "removed"; domain: string }
  | { kind: "error"; key: "invalidDomain" | "forbidden" | "failed" };

export function useScanExclusions() {
  const [items, setItems] = useState<ScanExclusion[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<ExclusionNotice | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const { exclusions } = await listScanExclusions();
      if (!alive.current) return;
      setItems(exclusions ?? []);
      setLoadFailed(false);
    } catch {
      if (alive.current) setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Resolves true when the site is on the list afterwards, so the form can clear. */
  const add = useCallback(
    async (domain: string, reason: string): Promise<boolean> => {
      setBusy(true);
      setNotice(null);
      try {
        const result = await addScanExclusion(domain, reason);
        if (!alive.current) return true;
        setNotice({ kind: result.created ? "added" : "already", domain: result.exclusion.domain });
        await load();
        return true;
      } catch (err) {
        if (alive.current) setNotice({ kind: "error", key: exclusionErrorKey(err) });
        return false;
      } finally {
        if (alive.current) setBusy(false);
      }
    },
    [load],
  );

  const remove = useCallback(
    async (domain: string): Promise<void> => {
      setBusy(true);
      setNotice(null);
      try {
        await removeScanExclusion(domain);
        if (alive.current) setNotice({ kind: "removed", domain });
      } catch (err) {
        if (alive.current) setNotice({ kind: "error", key: exclusionErrorKey(err) });
      } finally {
        // Read again either way: a 404 means somebody else already took it off.
        await load();
        if (alive.current) setBusy(false);
      }
    },
    [load],
  );

  return { items, loadFailed, busy, notice, add, remove, reload: load };
}
